import { FC, useMemo, useState, useEffect } from 'react';
import { useLoaderData } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpWideShort,
  faArrowDownWideShort,
  faMagnifyingGlass,
} from '@fortawesome/free-solid-svg-icons';
import MainContainer from '../../components/ui_components/MainContainer';
import { apiAddAdmin, apiRemoveAdmin, apiListAllUsers } from '../../components/ui_components/ApiList';
import colors from '../../styles/colors';
import type { RootState, UserListItem, UserRole } from '../../types';

const USERS_PER_PAGE = 10;

type SortField = 'username' | 'nickname_forum' | 'email';
type RoleFilter = 'all' | UserRole;

const ROLE_LABELS: Record<UserRole, string> = {
  founder: 'Founder',
  super_admin: 'Super Admin',
  admin: 'Admin',
  user: 'Utente',
};

const PageWrapper = styled.div`
  width: 100%;
  padding: 16px;
`;

const PageTitle = styled.h1`
  color: white;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 16px;
`;

const SearchBox = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 12px;

  & input {
    width: 100%;
    padding: 10px 12px 10px 36px;
    border: 2px solid ${colors.bgColorBordeaux};
    border-radius: 6px;
    background: ${colors.bgColorBlack2};
    color: #fff;
    font-size: 14px;
    outline: none;
  }

  & .search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #999;
    font-size: 14px;
  }
`;

const ToolbarRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const RoleSelect = styled.select`
  padding: 8px 12px;
  border: 2px solid ${colors.bgColorBordeaux};
  border-radius: 6px;
  background: ${colors.bgColorBlack2};
  color: #fff;
  font-size: 14px;
  flex: 1;
  min-width: 120px;
`;

const SortBtn = styled.button`
  padding: 8px 12px;
  border: 2px solid ${colors.bgColorBordeaux};
  border-radius: 6px;
  background: ${colors.bgColorBlack2};
  color: #fff;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
`;

const CardList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const UserCard = styled.li`
  background: ${colors.bgColorBlack2};
  border: 2px solid ${colors.bgColorBordeaux};
  border-radius: 8px;
  padding: 14px;
  color: white;

  & .card-username {
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 4px;
  }

  & .card-email {
    font-size: 13px;
    color: #aaa;
    margin-bottom: 4px;
  }

  & .card-forum {
    font-size: 13px;
    color: #aaa;
    margin-bottom: 8px;
  }

  & .role-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    margin-bottom: 8px;
  }

  & .role-founder,
  & .role-super_admin {
    background: gold;
    color: #000;
  }

  & .role-admin {
    background: ${colors.bgColorBordeaux};
    color: #fff;
  }

  & .role-user {
    background: #555;
    color: #fff;
  }

  & .wrestlers-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 6px;
  }

  & .wrestler-tag {
    background: rgba(163, 27, 27, 0.3);
    border: 1px solid ${colors.bgColorBordeaux};
    border-radius: 4px;
    padding: 2px 8px;
    font-size: 12px;
  }

  & .card-actions {
    margin-top: 10px;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
`;

const BtnAdmin = styled.button`
  padding: 6px 12px;
  font-size: 12px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  background: ${colors.bgColorBordeaux};
  color: #fff;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: darkgoldenrod;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.btn-remove {
    background: #dc3545;
  }

  &.btn-remove:hover:not(:disabled) {
    background: #c82333;
  }
`;

const PaginationBar = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  margin-top: 20px;
  flex-wrap: wrap;
`;

const PageBtn = styled.button<{ $active?: boolean }>`
  min-width: 36px;
  height: 36px;
  border: 2px solid ${(p) => (p.$active ? 'gold' : colors.bgColorBordeaux)};
  border-radius: 6px;
  background: ${(p) => (p.$active ? colors.bgColorBordeaux : colors.bgColorBlack2)};
  color: ${(p) => (p.$active ? 'gold' : '#fff')};
  font-size: 14px;
  cursor: pointer;
`;

const InfoText = styled.p`
  text-align: center;
  color: #999;
  font-size: 13px;
  margin-top: 12px;
`;

const EmptyState = styled.p`
  text-align: center;
  color: #999;
  padding: 40px 16px;
`;

const ListaUtentiMobile: FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const allUsers = useLoaderData() as UserListItem[];
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [sortField, setSortField] = useState<SortField>('username');
  const [sortDesc, setSortDesc] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersData, setUsersData] = useState<UserListItem[]>(allUsers);
  const [loadingAdminId, setLoadingAdminId] = useState<number | null>(null);

  const handleAddAdmin = async (userId: number): Promise<void> => {
    setLoadingAdminId(userId);
    try {
      await apiAddAdmin(userId);
      const refreshed = await apiListAllUsers();
      setUsersData(refreshed);
    } catch (error) {
      console.error('Errore durante aggiunta admin:', error);
    } finally {
      setLoadingAdminId(null);
    }
  };

  const handleRemoveAdmin = async (userId: number): Promise<void> => {
    setLoadingAdminId(userId);
    try {
      await apiRemoveAdmin(userId);
      const refreshed = await apiListAllUsers();
      setUsersData(refreshed);
    } catch (error) {
      console.error('Errore durante rimozione admin:', error);
    } finally {
      setLoadingAdminId(null);
    }
  };

  const handleSortClick = (field: SortField): void => {
    if (sortField === field) {
      setSortDesc(!sortDesc);
    } else {
      setSortField(field);
      setSortDesc(false);
    }
    setCurrentPage(1);
  };

  const processedUsers = useMemo(() => {
    let result = [...usersData];
    if (roleFilter !== 'all') {
      result = result.filter((u) => u.role === roleFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter((u) => {
        const fields = [
          u.username,
          u.nickname_forum,
          u.email,
          ...u.wrestlers.map((w) => w.name),
        ];
        return fields.some((f) => f?.toLowerCase().includes(q));
      });
    }
    result.sort((a, b) => {
      const valA = (a[sortField] ?? '').toString().toLowerCase();
      const valB = (b[sortField] ?? '').toString().toLowerCase();
      const cmp = valA.localeCompare(valB);
      return sortDesc ? -cmp : cmp;
    });
    return result;
  }, [usersData, roleFilter, searchQuery, sortField, sortDesc]);

  const totalPages = Math.max(1, Math.ceil(processedUsers.length / USERS_PER_PAGE));
  const paginatedUsers = processedUsers.slice(
    (currentPage - 1) * USERS_PER_PAGE,
    currentPage * USERS_PER_PAGE
  );

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    return (
      <PaginationBar>
        <PageBtn disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
          ‹
        </PageBtn>
        <span style={{ color: '#fff', padding: '0 8px' }}>
          {currentPage} / {totalPages}
        </span>
        <PageBtn
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(currentPage + 1)}
        >
          ›
        </PageBtn>
      </PaginationBar>
    );
  };

  return (
    <MainContainer>
      <PageWrapper>
        <PageTitle>Lista Utenti</PageTitle>

        <SearchBox>
          <FontAwesomeIcon icon={faMagnifyingGlass} className="search-icon" />
          <input
            type="text"
            placeholder="Cerca..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
          />
        </SearchBox>

        <ToolbarRow>
          <RoleSelect
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value as RoleFilter);
              setCurrentPage(1);
            }}
          >
            <option value="all">Tutti i ruoli</option>
            <option value="founder">Founder</option>
            <option value="super_admin">Super Admin</option>
            <option value="admin">Admin</option>
            <option value="user">Utente</option>
          </RoleSelect>
          <SortBtn onClick={() => handleSortClick('username')}>
            Nickname
            <FontAwesomeIcon
              icon={
                sortField === 'username' && sortDesc ? faArrowDownWideShort : faArrowUpWideShort
              }
              style={{ opacity: sortField === 'username' ? 1 : 0.4 }}
            />
          </SortBtn>
        </ToolbarRow>

        {processedUsers.length === 0 ? (
          <EmptyState>Nessun utente trovato</EmptyState>
        ) : (
          <>
            <CardList>
              {paginatedUsers.map((u) => (
                <UserCard key={u.id}>
                  <div className="card-username">{u.username}</div>
                  <div className="card-email">{u.email}</div>
                  <div className="card-forum">{u.nickname_forum ?? '-'}</div>
                  <span className={`role-badge role-${u.role}`}>{ROLE_LABELS[u.role]}</span>
                  <div className="wrestlers-list">
                    {u.wrestlers.length > 0
                      ? u.wrestlers.map((w) => (
                          <span key={w.id} className="wrestler-tag">
                            {w.name}
                          </span>
                        ))
                      : '-'}
                  </div>
                  {user?.is_super_admin && (
                    <div className="card-actions">
                      {u.role === 'user' && (
                        <BtnAdmin
                          disabled={loadingAdminId === u.id}
                          onClick={() => handleAddAdmin(u.id)}
                        >
                          {loadingAdminId === u.id ? '...' : 'Rendi Admin'}
                        </BtnAdmin>
                      )}
                      {u.role === 'admin' && (
                        <BtnAdmin
                          className="btn-remove"
                          disabled={loadingAdminId === u.id}
                          onClick={() => handleRemoveAdmin(u.id)}
                        >
                          {loadingAdminId === u.id ? '...' : 'Rimuovi Admin'}
                        </BtnAdmin>
                      )}
                    </div>
                  )}
                </UserCard>
              ))}
            </CardList>
            <InfoText>
              {processedUsers.length} utent{processedUsers.length === 1 ? 'e' : 'i'} — Pagina{' '}
              {currentPage} di {totalPages}
            </InfoText>
            {renderPagination()}
          </>
        )}
      </PageWrapper>
    </MainContainer>
  );
};

export default ListaUtentiMobile;
